import React, { useState, useEffect } from 'react';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const DateRoomSelection = ({ formData, onUpdate, onNext }) => {
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  const roomOptions = [
    { value: 'deluxe-forest-villa', label: 'Deluxe Forest Villa', description: '₹4,500/night - King bed, forest view' },
    { value: 'river-view-cottage', label: 'River View Cottage', description: '₹3,800/night - Queen bed, river view' },
    { value: 'luxury-tent', label: 'Luxury Tent', description: '₹2,900/night - Glamping experience' },
    { value: 'tree-house', label: 'Tree House', description: '₹5,200/night - Unique elevated stay' }
  ];

  const guestOptions = [
    { value: 1, label: '1 Guest', description: '1 Adult' },
    { value: 2, label: '2 Guests', description: '2 Adults' },
    { value: 3, label: '3 Guests', description: '2 Adults, 1 Child' },
    { value: 4, label: '4 Guests', description: '2 Adults, 2 Children' },
    { value: 5, label: '5 Guests', description: '3 Adults, 2 Children' },
    { value: 6, label: '6 Guests', description: '4 Adults, 2 Children' },
    { value: 7, label: '7+ Guests', description: 'Multiple Rooms Required' }
  ];
  
  const [guestDetails, setGuestDetails] = useState({
    adults: 2,
    children: 0,
    childrenAges: [],
    showCustomConfig: false
  });
  
  // Update guest details when guests value changes
  useEffect(() => {
    const selectedOption = guestOptions.find(option => option.value === formData?.guests);
    if (selectedOption) {
      if (selectedOption.value === 1) {
        setGuestDetails({ adults: 1, children: 0, childrenAges: [], showCustomConfig: false });
      } else if (selectedOption.value === 2) {
        setGuestDetails({ adults: 2, children: 0, childrenAges: [], showCustomConfig: false });
      } else if (selectedOption.value === 3) {
        setGuestDetails({ adults: 2, children: 1, childrenAges: [0], showCustomConfig: false });
      } else if (selectedOption.value === 4) {
        setGuestDetails({ adults: 2, children: 2, childrenAges: [0, 0], showCustomConfig: false });
      } else if (selectedOption.value === 5) {
        setGuestDetails({ adults: 3, children: 2, childrenAges: [0, 0], showCustomConfig: false });
      } else if (selectedOption.value === 6) {
        setGuestDetails({ adults: 4, children: 2, childrenAges: [0, 0], showCustomConfig: false });
      } else {
        setGuestDetails({ adults: 4, children: 3, childrenAges: [0, 0, 0], showCustomConfig: false });
      }
    }
  }, [formData?.guests]);
  
  // Update form data with detailed guest information
  useEffect(() => {
    if (formData?.guests) {
      onUpdate({ guestDetails: {
        adults: guestDetails.adults,
        children: guestDetails.children,
        childrenAges: guestDetails.childrenAges
      }});
    }
  }, [guestDetails.adults, guestDetails.children, guestDetails.childrenAges]);

  const handleCheckAvailability = () => {
    if (formData?.checkIn && formData?.checkOut && formData?.roomType) {
      setAvailabilityChecked(true);
    }
  };

  const calculateNights = () => {
    if (formData?.checkIn && formData?.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const getRoomPrice = () => {
    const room = roomOptions?.find(r => r?.value === formData?.roomType);
    if (!room) return 0;
    const priceMatch = room?.description?.match(/₹([\d,]+)/);
    return priceMatch ? parseInt(priceMatch?.[1]?.replace(',', '')) : 0;
  };

  const getTotalPrice = () => {
    return getRoomPrice() * calculateNights();
  };

  const isFormValid = () => {
    return formData?.checkIn && formData?.checkOut && formData?.roomType && formData?.guests && availabilityChecked;
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">
          Select Your Stay
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input
            label="Check-in Date"
            type="date"
            value={formData?.checkIn}
            onChange={(e) => onUpdate({ checkIn: e?.target?.value })}
            min={new Date()?.toISOString()?.split('T')?.[0]}
            required
          />

          <Input
            label="Check-out Date"
            type="date"
            value={formData?.checkOut}
            onChange={(e) => onUpdate({ checkOut: e?.target?.value })}
            min={formData?.checkIn || new Date()?.toISOString()?.split('T')?.[0]}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select
            label="Room Type"
            placeholder="Choose accommodation"
            options={roomOptions}
            value={formData?.roomType}
            onChange={(value) => onUpdate({ roomType: value })}
            required
          />

          <div className="space-y-2">
            <Select
              label="Number of Guests"
              placeholder="Select guests"
              options={guestOptions}
              value={formData?.guests}
              onChange={(value) => onUpdate({ guests: value })}
              required
            />
            
            {formData?.guests && (
              <div className="bg-muted/50 p-3 rounded text-sm">
                <div className="flex items-center justify-between text-muted-foreground mb-3">
                  <span>{guestOptions.find(opt => opt.value === formData?.guests)?.description}</span>
                  <button 
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => {
                      // Toggle custom guest configuration
                      setGuestDetails(prev => ({ ...prev, showCustomConfig: !prev.showCustomConfig }))
                    }}
                  >
                    {guestDetails.showCustomConfig ? 'Hide Details' : 'Customize'}
                  </button>
                </div>
                
                {guestDetails.showCustomConfig && (
                  <div className="space-y-3 pt-2 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-card-foreground">Adults</span>
                      <div className="flex items-center space-x-2">
                        <button 
                          type="button"
                          className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center hover:bg-muted-foreground/30"
                          onClick={() => {
                            if (guestDetails.adults > 1) {
                              setGuestDetails(prev => ({
                                ...prev,
                                adults: prev.adults - 1
                              }))
                            }
                          }}
                          disabled={guestDetails.adults <= 1}
                        >
                          <span>-</span>
                        </button>
                        <span className="w-6 text-center">{guestDetails.adults}</span>
                        <button 
                          type="button"
                          className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center hover:bg-muted-foreground/30"
                          onClick={() => {
                            if (guestDetails.adults + guestDetails.children < 8) {
                              setGuestDetails(prev => ({
                                ...prev,
                                adults: prev.adults + 1
                              }))
                            }
                          }}
                          disabled={guestDetails.adults + guestDetails.children >= 8}
                        >
                          <span>+</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-card-foreground">Children</span>
                      <div className="flex items-center space-x-2">
                        <button 
                          type="button"
                          className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center hover:bg-muted-foreground/30"
                          onClick={() => {
                            if (guestDetails.children > 0) {
                              setGuestDetails(prev => ({
                                ...prev,
                                children: prev.children - 1,
                                childrenAges: prev.childrenAges.slice(0, -1)
                              }))
                            }
                          }}
                          disabled={guestDetails.children <= 0}
                        >
                          <span>-</span>
                        </button>
                        <span className="w-6 text-center">{guestDetails.children}</span>
                        <button 
                          type="button"
                          className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center hover:bg-muted-foreground/30"
                          onClick={() => {
                            if (guestDetails.adults + guestDetails.children < 8) {
                              setGuestDetails(prev => ({
                                ...prev,
                                children: prev.children + 1,
                                childrenAges: [...prev.childrenAges, 0]
                              }))
                            }
                          }}
                          disabled={guestDetails.adults + guestDetails.children >= 8}
                        >
                          <span>+</span>
                        </button>
                      </div>
                    </div>
                    
                    {guestDetails.children > 0 && (
                      <div className="pt-2">
                        <p className="text-xs text-muted-foreground mb-2">Children's ages</p>
                        <div className="grid grid-cols-4 gap-2">
                          {guestDetails.childrenAges.map((age, index) => (
                            <select 
                              key={index}
                              className="text-xs p-1 border border-border rounded bg-background"
                              value={age}
                              onChange={(e) => {
                                const newAges = [...guestDetails.childrenAges];
                                newAges[index] = parseInt(e.target.value);
                                setGuestDetails(prev => ({
                                  ...prev,
                                  childrenAges: newAges
                                }))
                              }}
                            >
                              {[...Array(18)].map((_, i) => (
                                <option key={i} value={i}>{i} {i === 0 ? 'Infant' : i === 1 ? 'year' : 'years'}</option>
                              ))}
                            </select>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="pt-2">
                      <button
                        type="button"
                        className="w-full text-xs bg-primary/10 text-primary py-1 rounded hover:bg-primary/20"
                        onClick={() => {
                          // Find the closest guest option based on total guests
                          const totalGuests = guestDetails.adults + guestDetails.children;
                          const closestOption = guestOptions.find(opt => opt.value === totalGuests) || 
                                               guestOptions.find(opt => opt.value === 7);
                          
                          if (closestOption) {
                            onUpdate({ guests: closestOption.value });
                          }
                          
                          setGuestDetails(prev => ({ ...prev, showCustomConfig: false }))
                        }}
                      >
                        Apply Changes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {formData?.checkIn && formData?.checkOut && formData?.roomType && (
          <div className="bg-muted rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Duration:</span>
              <span className="font-medium text-card-foreground">
                {calculateNights()} night{calculateNights() > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">Room Rate:</span>
              <span className="font-medium text-card-foreground">
                ₹{getRoomPrice()?.toLocaleString('en-IN')}/night
              </span>
            </div>
            <div className="flex items-center justify-between text-lg font-semibold mt-3 pt-3 border-t border-border">
              <span className="text-card-foreground">Total:</span>
              <span className="text-primary">₹{getTotalPrice()?.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={handleCheckAvailability}
            disabled={!formData?.checkIn || !formData?.checkOut || !formData?.roomType}
            iconName="Search"
            iconPosition="left"
            className="flex-1"
          >
            Check Availability
          </Button>

          {availabilityChecked && (
            <div className="flex items-center space-x-2 text-success">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm font-medium">Available</span>
            </div>
          )}
        </div>
      </div>
      {/* Activities Add-on */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-heading font-semibold text-card-foreground mb-4">
          Add Activities (Optional)
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">River Rafting</p>
              <p className="text-sm text-muted-foreground">₹1,200 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('rafting')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'rafting'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'rafting') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>

          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">Jungle Safari</p>
              <p className="text-sm text-muted-foreground">₹800 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('safari')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'safari'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'safari') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">Kayaking</p>
              <p className="text-sm text-muted-foreground">₹900 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('kayaking')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'kayaking'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'kayaking') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">Zipline Adventure</p>
              <p className="text-sm text-muted-foreground">₹1,500 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('zipline')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'zipline'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'zipline') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">Nature Trek</p>
              <p className="text-sm text-muted-foreground">₹600 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('trek')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'trek'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'trek') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
            <div>
              <p className="font-medium text-card-foreground">Bird Watching</p>
              <p className="text-sm text-muted-foreground">₹400 per person</p>
            </div>
            <input
              type="checkbox"
              checked={formData?.activities?.includes('birdwatching')}
              onChange={(e) => {
                const activities = formData?.activities || [];
                if (e?.target?.checked) {
                  onUpdate({ activities: [...activities, 'birdwatching'] });
                } else {
                  onUpdate({ activities: activities?.filter(a => a !== 'birdwatching') });
                }
              }}
              className="w-4 h-4 text-primary"
            />
          </div>
        </div>
        
        {formData?.activities?.length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="font-medium text-sm text-card-foreground mb-2">Selected Activities:</p>
            <div className="flex flex-wrap gap-2">
              {formData?.activities?.includes('rafting') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">River Rafting</span>
              )}
              {formData?.activities?.includes('safari') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">Jungle Safari</span>
              )}
              {formData?.activities?.includes('kayaking') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">Kayaking</span>
              )}
              {formData?.activities?.includes('zipline') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">Zipline Adventure</span>
              )}
              {formData?.activities?.includes('trek') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">Nature Trek</span>
              )}
              {formData?.activities?.includes('birdwatching') && (
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">Bird Watching</span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={onNext}
          disabled={!isFormValid()}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Guest Details
        </Button>
      </div>
    </div>
  );
};

export default DateRoomSelection;